// お子さま情報に関する共通処理を定義
//

import { executeGetChild, executeGetChildren } from '../../dataAccess/webApi/dao/childrenDao'
import { fromApiYmd } from '../../utils/dateUtil'
import { nullPropsToUndefined } from '../../utils/objectUtil'
import { castNonNullable } from '../../utils/typeUtil'
import { yesNo } from './constant/classification'
import { AllergyForGetChildDto } from '../../dataAccess/webApi/dto/childrenDto'
import { GetChildrenHospitalAdmissionDto } from '../../dataAccess/webApi/dto/childrenHospitalAdmissionDto'
import { GetChildrenEatingDto } from '../../dataAccess/webApi/dto/childrenEatingDto'
import { PredispositionForGetChildDto } from '../../dataAccess/webApi/dto/childrenPredispositionDto'
import { GetChildrenDiseaseTestDto } from '../../dataAccess/webApi/dto/childrenDiseaseTestDto'
import { GetChildrenTreatmentDto } from '../../dataAccess/webApi/dto/childrenTreatmentDto'
import { GetChildrenDevelopmentDto } from '../../dataAccess/webApi/dto/childrenDevelopmentDto'
import { GetChildrenExcretionsDto } from '../../dataAccess/webApi/dto/childrenExcretionsDto'

/**
 * お子さま個別テーブル指定
 * selectコントロールで使用
 */
export const ADDITIONALS = {
  /** 入院 */
  HOSPITAL_ADMISSION: "hospitalAdmission",
  /** 食事 */
  EATING: "eating",
  /** 症状 */
  PREDISPOSITION: "predisposition",
  /** 検査 */
  DISEASE_TEST: "diseaseTest",
  /** 治療 */
  TREATMENT: "treatment",
  /** 発達 */
  DEVELOPMENT: "development",
  /** 排泄 */
  EXCRETIONS: "excretions",
} as const

/**
 * お子さまの選択で使用する要素に必要な情報。
 * selectコントロールで使用
 */
export interface ChildrenChoice {
  value: string
  label: string
}

/**
 * お子さまの選択で使用する要素に必要な情報。
 * selectコントロールで使用
 */
export interface InterviewedChildrenChoice {
  value: string
  label: string
  status: string
  permitFlag: string
  facilityId: string | null
  facilityName: string | null
}

/**
 * お子さま情報
 */
export interface Child {
  childId: string
  name: string
  kana: string
  birthday: Date
  brothersSistersFlag: string
  multipleBirthsFlag: string
  gender: string
  bloodType?: string
  maternalHandbookNo: string
  allergyFlag: string
  /** アレルギー１～３ */
  allergies: {
    causes?: string
    symptoms?: string
    treatments?: string
    concerns?: string
    excretions?: string
  }[]
  interviewPermitCount: number
  /** 排他チェック用の更新日時 */
  updateDatetime: string
  
  facilityId?: string
  birthHeight?:number
  birthWeight?:number
  normalTemperature?:number
  healthPhysicalDevelopmentConcerns?:string
  measurementDate?:string
  currentHeight?:number
  currentWeight?:number
  familyHospital1?: string
  familyHospitalDoctor1?: string
  familyHospitalTel1?: string
  familyHospital2?: string
  familyHospitalDoctor2?: string
  familyHospitalTel2?: string
  familyHospital3?: string
  familyHospitalDoctor3?: string
  familyHospitalTel3?: string
  usuallyMedicines?: string
  usuallyMedicinesMemo?: string
  bedTime?: string
  wakeupTime?: string
  napTime?: string
  napStartTime?: string
  napEndTime?: string
  putToSleep?: string
  fallAsleep?: string
  excretion?: string
  leaveChildFlag?: string
  leaveChildTo?: string
  normallyUsedFacilities?: string
  normallyUsedFacilitiesType?: string
  normallyUsedFacilitiesTel?: string
  normallyUsedFacilitiesAdmission?: string
  normallyUsedFacilitiesStartTime?: string
  normallyUsedFacilitiesEndTime?: string
  sameAddressFlag?: string
  tel?: string
  postalCode?: string
  address1?: string
  address2?: string
  buildingNameRoomNumber?: string
  medicalHistoryFlag?: string
  hospitalAdmissionFlag?: string
  febrileSeizureFlag?: string
  diseaseFlag?: string
  symptomFlag?: string
  vaccinationFlag?: string
  detailAllergyFlag?: string
  detailUpdateDatetime?: string
  
  allergy?: AllergyForGetChildDto[]
  hospitalAdmission?: GetChildrenHospitalAdmissionDto[]
  eating?: GetChildrenEatingDto
  predisposition?: PredispositionForGetChildDto[]
  diseaseTest?: GetChildrenDiseaseTestDto[]
  treatment?: GetChildrenTreatmentDto[]
  development?: GetChildrenDevelopmentDto
  excretions?: GetChildrenExcretionsDto[]
}

/**
 * 選択対象のお子さまに関する情報を返す。
 *
 * @param facilityId 施設ID。面談済みのお子さまのみを取得したい場合に指定する
 * @returns お子さま要素情報の配列
 */
export const getChildrenChoices = async (facilityId?: string): Promise<ChildrenChoice[]> => {
  const response = await executeGetChildren(...(facilityId ? [{ facilityId }] : []))
  return response.result.map((v) => ({ value: v.childId, label: v.name }))
}

/**
 * 面談済みのお子さまのみに関する情報を返す。
 * 面談未実施状態のお子さまを含める
 *
 * @param facilityId 施設ID。
 * @param getInterviewCrossManagementFlag 面談許可時の利用許可範囲取得フラグ。
 * @returns お子さま要素情報の配列
 */
export const getInterviewedChildrenChoices = async (
  facilityId: string,
  getInterviewCrossManagementFlag?: string
): Promise<InterviewedChildrenChoice[]> => {
  const response = await executeGetChildren({
    facilityId,
    includeNotImplementedFlag: yesNo.yes,
    getLatestInterviewedFlag: yesNo.yes,
    getInterviewCrossManagementFlag: getInterviewCrossManagementFlag ?? yesNo.no,
  })

  // 施設ID指定した場合、statusとpermitFlgはnullおよびundefinedはありえない
  return response.result.map((v) => ({
    value: v.childId,
    label: v.name,
    status: castNonNullable(v.status),
    permitFlag: castNonNullable(v.permitFlag),
    facilityId: castNonNullable(v.facilityId),
    facilityName: castNonNullable(v.facilityName),
  }))
}

/**
 * 指定したお子さまIDのお子さま情報を返す。
 *
 * @param childId お子さまID
 * @param facilityId 施設ID
 * @param additonals 追加取得する個別テーブルの指定
 * @returns お子さま情報
 */
export const getChild = async (childId: string, facilityId?: string, additonals?: string[]): Promise<Child> => {
  const response = await executeGetChild(childId, facilityId, additonals)
  const child = response.result
  const allergies = [
    {
      causes: child.allergyCauses1,
      symptoms: child.allergySymptoms1,
      treatments: child.allergyTreatments1,
      concerns: child.allergyConcerns1,
    },
    {
      causes: child.allergyCauses2,
      symptoms: child.allergySymptoms2,
      treatments: child.allergyTreatments2,
      concerns: child.allergyConcerns2,
    },
    {
      causes: child.allergyCauses3,
      symptoms: child.allergySymptoms3,
      treatments: child.allergyTreatments3,
      concerns: child.allergyConcerns3,
    },
  ].map((v) => nullPropsToUndefined(v))

  return nullPropsToUndefined({
    childId,
    name: child.name,
    kana: child.kana,
    birthday: fromApiYmd(child.birthday),
    brothersSistersFlag: child.brothersSistersFlag,
    multipleBirthsFlag: child.multipleBirthsFlag,
    gender: child.gender,
    bloodType: child.bloodType,
    maternalHandbookNo: child.maternalHandbookNo,
    allergyFlag: child.allergyFlag,
    allergies,
    updateDatetime: child.updateDatetime,
    interviewPermitCount: child.interviewPermitCount,
    
    facilityId: child.facilityId,
    birthHeight: child.birthHeight,
    birthWeight: child.birthWeight,
    normalTemperature: child.normalTemperature,
    healthPhysicalDevelopmentConcerns: child.healthPhysicalDevelopmentConcerns,
    measurementDate: child.measurementDate,
    currentHeight: child.currentHeight,
    currentWeight: child.currentWeight,
    familyHospital1: child.familyHospital1,
    familyHospitalDoctor1: child.familyHospitalDoctor1,
    familyHospitalTel1: child.familyHospitalTel1,
    familyHospital2: child.familyHospital2,
    familyHospitalDoctor2: child.familyHospitalDoctor2,
    familyHospitalTel2: child.familyHospitalTel2,
    familyHospital3: child.familyHospital3,
    familyHospitalDoctor3: child.familyHospitalDoctor3,
    familyHospitalTel3: child.familyHospitalTel3,
    usuallyMedicines: child.usuallyMedicines,
    usuallyMedicinesMemo: child.usuallyMedicinesMemo,
    bedTime: child.bedTime,
    wakeupTime: child.wakeupTime,
    napTime: child.napTime,
    napStartTime: child.napStartTime,
    napEndTime: child.napEndTime,
    putToSleep: child.putToSleep,
    fallAsleep: child.fallAsleep,
    excretion: child.excretion,
    leaveChildFlag: child.leaveChildFlag,
    leaveChildTo: child.leaveChildTo,
    normallyUsedFacilities: child.normallyUsedFacilities,
    normallyUsedFacilitiesType: child.normallyUsedFacilitiesType,
    normallyUsedFacilitiesTel: child.normallyUsedFacilitiesTel,
    normallyUsedFacilitiesAdmission: child.normallyUsedFacilitiesAdmission,
    normallyUsedFacilitiesStartTime: child.normallyUsedFacilitiesStartTime,
    normallyUsedFacilitiesEndTime: child.normallyUsedFacilitiesEndTime,
    sameAddressFlag: child.sameAddressFlag,
    tel: child.tel,
    postalCode: child.postalCode,
    address1: child.address1,
    address2: child.address2,
    buildingNameRoomNumber: child.buildingNameRoomNumber,
    medicalHistoryFlag: child.medicalHistoryFlag,
    hospitalAdmissionFlag: child.hospitalAdmissionFlag,
    febrileSeizureFlag: child.febrileSeizureFlag,
    diseaseFlag: child.diseaseFlag,
    symptomFlag: child.symptomFlag,
    vaccinationFlag: child.vaccinationFlag,
    detailAllergyFlag: child.detailAllergyFlag,
    detailUpdateDatetime: child.detailUpdateDatetime,
    
    allergy: child.allergy,
    hospitalAdmission: child.hospitalAdmission,
    eating: child.eatingResult,
    predisposition: child.predisposition,
    diseaseTest: child.diseaseTest,
    treatment: child.treatment,
    development: child.developmentResult,
    excretions: child.excretions,
  })
}
